import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface Entity {
  id: string;
  name: string;
  type: string;
  taxId: string;
  address: string;
  contactPerson: string;
  email: string;
  phone: string;
}

const initialEntityState: Entity = {
  id: '',
  name: '',
  type: '',
  taxId: '',
  address: '',
  contactPerson: '',
  email: '',
  phone: '',
};

const Entities: React.FC = () => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [open, setOpen] = useState(false);
  const [currentEntity, setCurrentEntity] = useState<Entity>(initialEntityState);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await axios.get('/api/entities');
      setEntities(response.data);
    } catch (error) {
      console.error('Error fetching entities:', error);
      showSnackbar('Error fetching entities', 'error');
    }
  };

  const handleOpen = (entity?: Entity) => {
    if (entity) {
      setCurrentEntity(entity);
      setIsEditing(true);
    } else {
      setCurrentEntity(initialEntityState);
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentEntity(initialEntityState);
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentEntity(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await axios.put(`/api/entities/${currentEntity.id}`, currentEntity);
        showSnackbar('Entity updated successfully');
      } else {
        await axios.post('/api/entities', currentEntity);
        showSnackbar('Entity added successfully');
      }
      handleClose();
      fetchEntities();
    } catch (error) {
      console.error('Error saving entity:', error);
      showSnackbar('Error saving entity', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entity?')) {
      try {
        await axios.delete(`/api/entities/${id}`);
        showSnackbar('Entity deleted successfully');
        fetchEntities();
      } catch (error) {
        console.error('Error deleting entity:', error);
        showSnackbar('Error deleting entity', 'error');
      }
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Entities
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Entity
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Tax ID</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.map((entity) => (
              <TableRow key={entity.id}>
                <TableCell>{entity.name}</TableCell>
                <TableCell>{entity.type}</TableCell>
                <TableCell>{entity.taxId}</TableCell>
                <TableCell>{entity.contactPerson}</TableCell>
                <TableCell>{entity.email}</TableCell>
                <TableCell>{entity.phone}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(entity)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(entity.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Entity' : 'Add Entity'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              name="name"
              value={currentEntity.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Type"
              name="type"
              value={currentEntity.type}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Tax ID"
              name="taxId"
              value={currentEntity.taxId}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Address"
              name="address"
              value={currentEntity.address}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Contact Person"
              name="contactPerson"
              value={currentEntity.contactPerson}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={currentEntity.email}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Phone"
              name="phone"
              value={currentEntity.phone}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Entities; 